"use client";

import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from '@/lib/api';
import { useAuth } from '../context/AuthContext';
import { Search, MoreHorizontal, Edit, Trash2, Users, Shield, Key, FileText, Plus, Loader2, ExternalLink, Copy, BookPlus } from "lucide-react";

interface Device {
  id: string;
  uuid: string;
  name: string;
  group: string;
  groupId: number | null;
  password: string;
  notes: string;
  tags: string[];
  ip: string;
  version: string;
  os: string;
  lastSeen: string | null;
  online: boolean;
  createdAt: string;
  updatedAt: string;
}

export function DevicesPage() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", notes: "", tags: "", groupId: "" });

  const { data: devicesData, isLoading } = useQuery({
    queryKey: ["devices"],
    queryFn: () => api.getDevices(token!),
    enabled: !!token,
    refetchInterval: 30000,
  });

  const { data: groups } = useQuery({
    queryKey: ["groups"],
    queryFn: () => api.getGroups(token!),
    enabled: !!token,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => api.updateDevice(id, data, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["devices"] });
      setEditDialogOpen(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteDevice(id, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["devices"] });
    },
  });

  const bulkAddMutation = useMutation({
    mutationFn: (deviceIds: string[]) => api.bulkAddToAddressBook(deviceIds, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["addressBook"] });
    },
  });

  const handleEdit = (device: Device) => {
    setSelectedDevice(device);
    setEditForm({
      name: device.name,
      notes: device.notes,
      tags: device.tags.join(", "),
      groupId: device.groupId?.toString() || "",
    });
    setEditDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedDevice) {
      updateMutation.mutate({
        id: selectedDevice.id,
        data: {
          name: editForm.name,
          notes: editForm.notes,
          tags: editForm.tags.split(",").map(t => t.trim()).filter(Boolean),
          groupId: editForm.groupId ? parseInt(editForm.groupId) : null,
        },
      });
    }
  };

  const filteredDevices = devicesData?.devices?.filter(
    (d: Device) =>
      d.name.toLowerCase().includes(search.toLowerCase()) ||
      d.id.toLowerCase().includes(search.toLowerCase()) ||
      d.group.toLowerCase().includes(search.toLowerCase()) ||
      d.ip.toLowerCase().includes(search.toLowerCase())
  ) || [];

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-6">
            <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
            <div className="mt-4 h-8 w-1/2 animate-pulse rounded bg-muted" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Devices</h1>
          <p className="text-muted-foreground">Manage and monitor connected devices</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle>Device List</CardTitle>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Search devices..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="hidden sm:table-cell">Status</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="hidden md:table-cell">ID</TableHead>
                <TableHead className="hidden lg:table-cell">Group</TableHead>
                <TableHead className="hidden sm:table-cell">IP</TableHead>
                <TableHead className="hidden lg:table-cell">OS / Version</TableHead>
                <TableHead className="hidden sm:table-cell">Last Seen</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDevices.map((device: any) => (
                <TableRow key={device.id}>
                  <TableCell className="hidden sm:table-cell">
                    <Badge variant={device.online ? "success" : "secondary"} className="w-full">
                      {device.online ? "Online" : "Offline"}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <Badge variant={device.online ? "success" : "secondary"} className="hidden sm:inline-flex">
                        {device.online ? "Online" : "Offline"}
                      </Badge>
                      <span>{device.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs hidden md:table-cell">{device.id.slice(0, 12)}...</TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <Badge variant="secondary">{device.group}</Badge>
                  </TableCell>
                  <TableCell className="font-mono text-sm hidden sm:table-cell">{device.ip}</TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <div className="text-sm">{device.os}</div>
                    <div className="text-xs text-muted-foreground">{device.version}</div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground hidden sm:table-cell">
                    {device.lastSeen ? new Date(device.lastSeen).toLocaleString() : "Never"}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => {
                          const uri = `rustdesk://${device.id}${device.password !== '***' ? `?password=${encodeURIComponent(device.password)}` : ''}`;
                          window.open(uri, '_blank');
                        }}>
                          <ExternalLink className="mr-2 h-4 w-4" />
                          Connect (Open RustDesk)
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => {
                          navigator.clipboard.writeText(device.id);
                        }}>
                          <Copy className="mr-2 h-4 w-4" />
                          Copy RustDesk ID
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => bulkAddMutation.mutate([device.id])}>
                          <BookPlus className="mr-2 h-4 w-4" />
                          Add to Address Book
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleEdit(device)}>
                          <Edit className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => console.log("View details", device.id)}>
                          <FileText className="mr-2 h-4 w-4" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => deleteMutation.mutate(device.id)} className="text-destructive focus:text-destructive">
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {filteredDevices.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No devices found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Device</DialogTitle>
            <DialogDescription>Update device information and group assignment</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="groupId">Group</Label>
                <select
                  id="groupId"
                  value={editForm.groupId}
                  onChange={(e) => setEditForm({ ...editForm, groupId: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">No Group</option>
                  {groups?.map((g: any) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Input
                  id="notes"
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tags">Tags (comma separated)</Label>
                <Input
                  id="tags"
                  value={editForm.tags}
                  onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}